import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tab } from '@headlessui/react';
import { sortDebtorsPrimaryFirst } from '../../utils/debtors';
const API = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("accessToken");
    const calcMonthlyIncome = (rate, schedule, units) => {
    const r = Number(rate);
    const u = units ? Number(units) : null;
    switch (schedule) {
      case 'hourly':
        // assume units = hours per week
        const hrs = u !== null ? u : 40;
        return (r * hrs * 52) / 12;
      case 'weekly':
        return (r * 52) / 12;
      case 'biweekly':
        return (r * 26) / 12;
      case 'semimonthly':
        return r * 2;
      case 'monthly':
        return r;
      case 'yearly':
        return r / 12;
      default:
        return 0;
    }
  };

export default function IncomeSection({ items, caseId, caseData, onAdd, onEdit, onDelete }) {
  const [showEditIncome, setShowEditIncome] = useState(false);
  const [editingIncomeData, setEditingIncomeData] = useState([]);
  const saveIncomeRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  if (!caseData) return null;
    const orderedDebtors = sortDebtorsPrimaryFirst(caseData.debtors);
    return ( 
      <div>
        {/* Income */}
        <div className="bg-white rounded-lg shadow-2xl p-6 mx-4 sm:mx-auto sm:max-w-[1200px]">
          <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">1. Income</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {orderedDebtors.map(d => {
              const fullName = [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
              return (
                <div key={d.id}>
                  <p className="font-semibold">{fullName}{!d.is_filing && ' (Non-filing spouse)'}</p>
                  {/* Employment Income */}
                  <div className="mb-2">
                    <h4 className="font-medium">Employment Income</h4>
                    <ul className="ml-4 list-disc list-inside text-gray-700">
                      {d.employments.map(emp => {
                        const monthlyIncome = calcMonthlyIncome(emp.pay_rate, emp.pay_schedule, emp.pay_units);
                        // Now, treat deduction amounts as already monthly
                        const totalMonthlyDeductions = (emp.deductions || []).reduce(
                          (sum, det) => sum + Number(det.amount || 0),
                          0
                        );
                        const netMonthly = monthlyIncome - totalMonthlyDeductions;
                        return (
                          <li key={emp.id}>
                            {emp.employer}: {emp.pay_rate} {emp.pay_schedule} — Monthly Income: ${monthlyIncome.toFixed(2)}
                            <div className="ml-4">
                              Payment Advices/Pay Stubs: {emp.has_payment_advices === false ? 'No' : 'Yes'}
                            </div>
                            {emp.deductions?.length > 0 && (
                              <div className="ml-4">
                                <p>Total Monthly Deductions: ${totalMonthlyDeductions.toFixed(2)}</p>
                                <ul className="ml-4 list-disc list-inside">
                                  {emp.deductions.map(det => (
                                    <li key={det.id}>
                                      {det.deduction_type.replace(/_/g,' ').replace(/\b\w/g,c => c.toUpperCase())}: ${det.amount} monthly
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <span className="ml-4 font-semibold">Net Monthly Income: ${netMonthly.toFixed(2)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {/* Fixed Income Sources */}
                  <div className="mb-2">
                    <h4 className="font-medium">Fixed Income</h4>
                    <ul className="ml-4 list-disc list-inside text-gray-700">
                      {d.interest_dividend_income && <li>Interest/Dividends: ${d.interest_dividend_income}</li>}
                      {d.family_support_payments && <li>Family Support: ${d.family_support_payments}</li>}
                      {d.unemployment_income && <li>Unemployment: ${d.unemployment_income}</li>}
                      {d.social_security_income && <li>Social Security: ${d.social_security_income}</li>}
                      {d.pension_income && <li>Pension: ${d.pension_income}</li>}
                    </ul>
                  </div>
                  {/* Government Assistance */}
                  {d.gov_assistances.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Government Assistance</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.gov_assistances.map(ga => (
                          <li key={ga.id}>{ga.name}: ${ga.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Other Income Items */}
                  {d.other_income_items.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Other Income</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.other_income_items.map(oi => (
                          <li key={oi.id}>{oi.name}: ${oi.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Expense Contributions */}
                  {d.expense_contributions.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Expense Contributions</h4>
                      <ul className="ml-4 list-disc list-inside text-gray-700">
                        {d.expense_contributions.map(ec => (
                          <li key={ec.id}>{ec.name}: ${ec.amount}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Business Income */}
                  {d.business_incomes.length > 0 && (
                    <div className="mb-2">
                      <h4 className="font-medium">Business Income</h4>
                      {d.business_incomes.map(bi => {
                        const totalExpenses = bi.expenses.reduce((sum, e) => sum + Number(e.amount), 0);
                        const netIncome = Number(bi.gross_revenue) - totalExpenses;
                        return (
                          <div key={bi.id} className="ml-4 mb-1">
                            <p>{bi.name}: Gross ${bi.gross_revenue}, Expenses ${totalExpenses}, Net ${netIncome}</p>
                            {bi.expenses.length > 0 && (
                              <ul className="ml-4 list-disc list-inside text-gray-700">
                                {bi.expenses.map(exp => (
                                  <li key={exp.id}>{exp.expense_name}: ${exp.amount}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={() => {
              setEditingIncomeData(orderedDebtors.map(d => ({ ...d })));
              setShowEditIncome(true);
            }}
            className="mt-4 bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700"
          >
            Edit Income
          </button>
        </div>
      {showEditIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto relative">
            {isSaving && (
              <div className="absolute top-3 right-4 text-sm text-gray-500 flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"></path>
                </svg>
                Saving…
              </div>
            )}
            <h2 className="text-xl font-bold mb-4">Edit Income</h2>
            {/* Tabbed Income Editing */}
            <Tab.Group>
              <Tab.List className="flex space-x-2 border-b mb-4">
                {['Employments','Fixed Income','Business Income'].map(tab => (
                  <Tab key={tab} className={({ selected }) =>
                    `px-3 py-1 rounded-t ${selected ? 'bg-white border-t border-l border-r' : 'bg-gray-200'}`
                  }>
                    {tab}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
  {editingIncomeData.map((debtor, di) => (
    <div key={di} className="mb-6 border p-4 rounded">
      <h4 className="font-semibold mb-4">
        {[debtor.first_name, debtor.second_name, debtor.last_name].filter(Boolean).join(' ')} — Employments
      </h4>

      {/* For each employment record: */}
      {debtor.employments.map((emp, ei) => (
        <div key={ei} className="mb-4 border p-3 rounded">
          {/* Row 2: Employer, Occupation, Start Date, End Date, Length */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            {/* Employer */}
            <div>
              <label className="block text-sm font-medium">Employer</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm lg:col-span-3"
                value={emp.employer || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Occupation */}
            <div>
              <label className="block text-sm font-medium">Occupation</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm lg:col-span-2"
                value={emp.occupation || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].occupation = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium">Start Date</label>
              <input
                type="date"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.start_date || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].start_date = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* End Date */}
            <div>
              <label className="block text-sm font-medium">End Date</label>
              <input
                type="date"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.end_date || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].end_date = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Length */}
            <div>
              <label className="block text-sm font-medium">Length of Employment</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                maxLength={50}
                placeholder="e.g., 1 year or 3 months"
                value={emp.length_of_employment || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].length_of_employment = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Row 1: Street, Street 2, City, State, ZIP */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            {/* Street */}
            <div>
              <label className="block text-sm font-medium">Street</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_street || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_street = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Street 2 */}
            <div>
              <label className="block text-sm font-medium">Street 2</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_street2 || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_street2 = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* City */}
            <div>
              <label className="block text-sm font-medium">City</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.employer_city || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_city = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* State */}
            <div>
              <label className="block text-sm font-medium">State</label>
              <input
                type="text"
                maxLength={2}
                className="mt-1 block w-full border rounded px-1 py-1 text-sm w-16"
                value={emp.employer_state || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_state = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* ZIP */}
            <div>
              <label className="block text-sm font-medium">ZIP</label>
              <input
                type="text"
                maxLength={5}
                className="mt-1 block w-full border rounded px-1 py-1 text-sm w-16"
                value={emp.employer_zip || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].employer_zip = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Row 3: Pay Type, Pay Schedule, Rate, Units */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            {/* Pay Type */}
            <div>
              <label className="block text-sm font-medium">Pay Type</label>
              <select className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_type || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_type = e.target.value;
                  setEditingIncomeData(upd);
                }}
              >
                <option value="" disabled>Select</option>
                <option value="hourly">Hourly</option>
                <option value="salary">Salary</option>
                <option value="self-employed">Self-Employed</option>
              </select>
            </div>
            {/* Pay Schedule */}
            <div>
              <label className="block text-sm font-medium">Pay Schedule</label>
              <select className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_schedule || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_schedule = e.target.value;
                  setEditingIncomeData(upd);
                }}
              >
                <option value="" disabled>Select</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="semimonthly">Semimonthly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            {/* Pay Rate */}
            <div>
              <label className="block text-sm font-medium">Rate</label>
              <input type="number" step="0.01" className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_rate || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_rate = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            {/* Pay Units */}
            <div>
              <label className="block text-sm font-medium">Units</label>
              <input type="text" className="mt-1 block w-full border rounded px-1 py-1 text-sm"
                value={emp.pay_units || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].employments[ei].pay_units = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          <label className="mt-3 flex items-start gap-2 rounded border border-blue-200 bg-blue-50 p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={emp.has_payment_advices !== false}
              onChange={e => {
                const upd = [...editingIncomeData];
                upd[di].employments[ei].has_payment_advices = e.target.checked;
                setEditingIncomeData(upd);
              }}
            />
            <span>
              <span className="block text-sm font-medium">Has payment advices/pay stubs</span>
              <span className="block text-xs text-gray-600">
                This controls the debtor’s answer on the Payment Advices PDF.
              </span>
            </span>
          </label>

          {/* Deductions Section */}
          <div className="mt-4">
            <h5 className="font-medium mb-2">Deductions</h5>
            {emp.deductions.map((det, di2) => (
              <div key={di2} className="flex space-x-2 items-end mb-2">
                <select
                  className="border rounded px-2 py-1"
                  value={det.deduction_type}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions[di2].deduction_type = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                >
                  <option value="" disabled>Type</option>
                  {['taxes','mandatory_retirement','voluntary_retirement','retirement_loan','insurance','dso','union_dues','other'].map(opt => (
                    <option key={opt} value={opt}>{opt.replace(/_/g,' ')}</option>
                  ))}
                </select>
                {det.deduction_type === 'other' && (
                  <input
                    type="text"
                    placeholder="Description"
                    className="border rounded px-2 py-1 flex-1"
                    value={det.other_description || ''}
                    onChange={e => {
                      const upd = [...editingIncomeData];
                      upd[di].employments[ei].deductions[di2].other_description = e.target.value;
                      setEditingIncomeData(upd);
                    }}
                  />
                )}
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  className="w-24 border rounded px-2 py-1"
                  value={det.amount || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions[di2].amount = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <button
                  className="text-red-600"
                  onClick={() => {
                    const upd = [...editingIncomeData];
                    upd[di].employments[ei].deductions = upd[di].employments[ei].deductions.filter((_, k) => k !== di2);
                    setEditingIncomeData(upd);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="text-blue-600 text-sm"
              onClick={() => {
                const upd = [...editingIncomeData];
                upd[di].employments[ei].deductions.push({ deduction_type:'', other_description:'', amount:'' });
                setEditingIncomeData(upd);
              }}
            >
              + Add Deduction
            </button>
          </div>

          {/* Remove Employment */}
          <button
            className="mt-3 text-red-600"
            onClick={() => {
              const upd = [...editingIncomeData];
              upd[di].employments = upd[di].employments.filter((_, k) => k !== ei);
              setEditingIncomeData(upd);
            }}
          >
            Remove Employment
          </button>
        </div>
      ))}

      {/* Add Employment */}
      <button
        className="text-blue-600 text-sm"
        onClick={() => {
          const upd = [...editingIncomeData];
          upd[di].employments.push({
            employer:'', occupation:'', employer_street:'', employer_street2:'',
            employer_city:'', employer_state:'', employer_zip:'',
            length_of_employment:'', start_date:'', end_date:'',
            pay_type:'', pay_schedule:'', pay_units:'', pay_rate:'',
            has_payment_advices:true, deductions:[]
          });
          setEditingIncomeData(upd);
        }}
      >
        + Add Employment
      </button>
    </div>
  ))}
                </Tab.Panel>
                {/* Fixed Income Tab Panel */}
                <Tab.Panel>
                  <div className="space-y-3">
                    {editingIncomeData.map((debtorData, idx) => {
                      const fullName = [debtorData.first_name, debtorData.second_name, debtorData.last_name]
                        .filter(Boolean).join(' ');
                      return (
                        <div key={idx} className="border-b pb-4">
                          <h4 className="text-md font-semibold mb-2">{fullName} — Fixed Income</h4>

                          {/* Single‐value fixed incomes in a responsive grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                            {[
                              'interest_dividend_income',
                              'family_support_payments',
                              'unemployment_income',
                              'social_security_income',
                              'pension_income'
                            ].map(field => (
                              <div key={field}>
                                <label className="block text-sm font-medium text-gray-700 capitalize">
                                  {field.replace(/_/g,' ')}
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="mt-1 block w-full border rounded px-2 py-1"
                                  value={debtorData[field] ?? ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    const updated = editingIncomeData.map((d,i) =>
                                      i===idx ? {...d, [field]: val} : d
                                    );
                                    setEditingIncomeData(updated);
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Combine list categories into one row */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Government Assistance panel */}
                            <div>
                              <h5 className="font-medium mb-2">Government Assistance</h5>
                              {debtorData.gov_assistances.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Program Name"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.gov_assistances];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i) =>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.gov_assistances];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i) =>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.gov_assistances.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, gov_assistances:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.gov_assistances, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, gov_assistances:list} : d
                                  ));
                                }}
                              >
                                + Add Assistance
                              </button>
                            </div>
                            {/* Other Income panel */}
                            <div>
                              <h5 className="font-medium mb-2">Other Income</h5>
                              {debtorData.other_income_items.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Description"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.other_income_items];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.other_income_items];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.other_income_items.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, other_income_items:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.other_income_items, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, other_income_items:list} : d
                                  ));
                                }}
                              >
                                + Add Other Income
                              </button>
                            </div>
                            {/* Expense Contributions panel */}
                            <div>
                              <h5 className="font-medium mb-2">Expense Contributions</h5>
                              {debtorData.expense_contributions.map((item,j) => (
                                <div key={j} className="grid grid-cols-6 gap-2 items-end mb-2">
                                  <input
                                    type="text"
                                    placeholder="Source"
                                    className="col-span-3 border rounded px-2 py-1"
                                    value={item.name}
                                    onChange={e => {
                                      const list = [...debtorData.expense_contributions];
                                      list[j].name = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Amount"
                                    className="col-span-2 border rounded px-2 py-1"
                                    value={item.amount}
                                    onChange={e => {
                                      const list = [...debtorData.expense_contributions];
                                      list[j].amount = e.target.value;
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  />
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const list = debtorData.expense_contributions.filter((_,k)=>k!==j);
                                      setEditingIncomeData(editingIncomeData.map((d,i)=>
                                        i===idx ? {...d, expense_contributions:list} : d
                                      ));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                className="text-blue-600 text-sm"
                                onClick={() => {
                                  const list = [...debtorData.expense_contributions, {name:'',amount:''}];
                                  setEditingIncomeData(editingIncomeData.map((d,i)=>
                                    i===idx ? {...d, expense_contributions:list} : d
                                  ));
                                }}
                              >
                                + Add Contribution
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
  {editingIncomeData.map((debtor, di) => (
    <div key={di} className="mb-6 border p-4 rounded">
      <h4 className="font-semibold mb-4">
        {[debtor.first_name, debtor.second_name, debtor.last_name]
          .filter(Boolean)
          .join(' ')} — Business Income
      </h4>

      {/* Business Income Records */}
      {debtor.business_incomes.map((biz, bi) => (
        <div key={biz.id || bi} className="mb-6 border p-3 rounded">
          {/* Name & Gross Revenue */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            <div>
              <label className="block text-sm font-medium">Business Name</label>
              <input
                type="text"
                className="mt-1 block w-full border rounded px-2 py-1 text-sm"
                value={biz.name || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].business_incomes[bi].name = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Gross Revenue</label>
              <input
                type="number"
                step="0.01"
                className="mt-1 block w-full border rounded px-2 py-1 text-sm"
                value={biz.gross_revenue || ''}
                onChange={e => {
                  const upd = [...editingIncomeData];
                  upd[di].business_incomes[bi].gross_revenue = e.target.value;
                  setEditingIncomeData(upd);
                }}
              />
            </div>
          </div>

          {/* Expenses */}
          <div className="mb-4">
            <h5 className="font-medium mb-2">Expenses</h5>
            {biz.expenses.map((exp, ei) => (
              <div key={exp.id || ei} className="grid grid-cols-6 gap-2 items-end mb-2">
                <input
                  type="text"
                  placeholder="Expense Name"
                  className="col-span-4 border rounded px-2 py-1 text-sm"
                  value={exp.expense_name || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses[ei].expense_name = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  className="col-span-1 border rounded px-2 py-1 text-sm"
                  value={exp.amount || ''}
                  onChange={e => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses[ei].amount = e.target.value;
                    setEditingIncomeData(upd);
                  }}
                />
                <button
                  className="text-red-600"
                  onClick={() => {
                    const upd = [...editingIncomeData];
                    upd[di].business_incomes[bi].expenses = upd[di]
                      .business_incomes[bi]
                      .expenses.filter((_, idx) => idx !== ei);
                    setEditingIncomeData(upd);
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="text-blue-600 text-sm"
              onClick={() => {
                const upd = [...editingIncomeData];
                upd[di].business_incomes[bi].expenses.push({ expense_name: '', amount: '' });
                setEditingIncomeData(upd);
              }}
            >
              + Add Expense
            </button>
          </div>

          {/* Remove Business Income */}
          <button
            className="mt-2 text-red-600"
            onClick={() => {
              const upd = [...editingIncomeData];
              upd[di].business_incomes = upd[di].business_incomes.filter((_, idx) => idx !== bi);
              setEditingIncomeData(upd);
            }}
          >
            Remove Business
          </button>
        </div>
      ))}

      {/* Add Business Income */}
      <button
        className="text-blue-600 text-sm"
        onClick={() => {
          const upd = [...editingIncomeData];
          upd[di].business_incomes.push({ name: '', gross_revenue: '', expenses: [] });
          setEditingIncomeData(upd);
        }}
      >
        + Add Business Income
      </button>
    </div>
  ))}
  </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    // Persist per-debtor edits
                    for (const debtorData of editingIncomeData) {
                      // 1) employments (and nested deductions)
                      const empResp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/employments/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ employments: debtorData.employments || [] }),
                        }
                      );
                      if (!empResp.ok) throw new Error('Failed to save employments for ' + debtorData.id);

                      // 2) fixed/other income buckets
                      const incomePayload = {
                        interest_dividend_income: debtorData.interest_dividend_income ?? null,
                        family_support_payments: debtorData.family_support_payments ?? null,
                        unemployment_income: debtorData.unemployment_income ?? null,
                        social_security_income: debtorData.social_security_income ?? null,
                        pension_income: debtorData.pension_income ?? null,
                        gov_assistances: debtorData.gov_assistances || [],
                        other_income_items: debtorData.other_income_items || [],
                        expense_contributions: debtorData.expense_contributions || [],
                      };
                      const incResp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/income/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify(incomePayload),
                        }
                      );
                      if (!incResp.ok) throw new Error('Failed to save income for ' + debtorData.id);

                      // 3) business incomes
                      const bizResp = await fetch(
                        `${API}/case/${caseId}/debtors/${debtorData.id}/business-incomes/`,
                        {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ business_incomes: debtorData.business_incomes || [] }),
                        }
                      );
                      if (!bizResp.ok) throw new Error('Failed to save business income for ' + debtorData.id);
                    }

                    // After all saves, refetch the full, canonical overview so UI reflects backend truth
                    const overviewResp = await fetch(
                      `${API}/case/${caseId}/overview/`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!overviewResp.ok) throw new Error('Failed to reload case overview');
                    const freshOverview = await overviewResp.json();

                    // Notify parent with fresh data (includes debtors with all nested relations)
                    onEdit({ ...freshOverview });

                    setShowEditIncome(false);
                  } catch (err) {
                    console.error(err);
                    alert('Failed to save income: ' + err.message);
                  } finally {
                    setIsSaving(false);
                  }
                }}
                className={`px-4 py-2 rounded text-white ${isSaving ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                ref={saveIncomeRef}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="inline-flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"></path>
                    </svg>
                    Saving…
                  </span>
                ) : 'Save'}
              </button>
              <button
                onClick={() => setShowEditIncome(false)}
                className={`px-4 py-2 rounded ${isSaving ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300'}`}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
            {/* Overlay to block interaction while saving */}
            {isSaving && (
              <div className="absolute inset-0 bg-white bg-opacity-40 pointer-events-auto"></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
IncomeSection.propTypes = {
    items: PropTypes.array.isRequired,
    caseId: PropTypes.string.isRequired,
    caseData: PropTypes.object.isRequired,
    onAdd: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
  };
